/**
 * The product events that Riven Auth Cloud uses for analytics and auditing.
 *
 * - All events should be in past tense, with the format of `<noun> <verb>`.
 * - Unless otherwise specified, all events should contain tenant ID as the
 *   `tenant` group distinct ID.
 *
 * @remarks
 * Events that are tracked in the cloud service will be marked with `@cloud`.
 */
export var ProductEvent;
(function (ProductEvent) {
    /** @cloud */
    ProductEvent["TenantCreated"] = "tenant created";
    /** @cloud */
    ProductEvent["TenantDeleted"] = "tenant deleted";
    /**
     * One or more collaborators have been invited to the Riven Auth Cloud tenant.
     *
     * @cloud
     */
    ProductEvent["CollaboratorInvited"] = "collaborator invited";
    /**
     * The Riven Auth Cloud tenant has subscribed to the Pro plan. It may be the first time subscribing,
     * switching from the Free plan, or converting from a dev tenant, etc.
     *
     * @cloud
     */
    ProductEvent["ProPlanSubscribed"] = "pro plan subscribed";
    /**
     * The Riven Auth Cloud tenant has canceled the Pro plan.
     *
     * @cloud
     */
    ProductEvent["ProPlanCanceled"] = "pro plan canceled";
    /**
     * The Riven Auth Cloud tenant has subscribed to the Free plan. This may happen when a tenant
     * newly created or downgrading from the Pro plan.
     *
     * @cloud
     */
    ProductEvent["FreePlanSubscribed"] = "free plan subscribed";
    /**
     * A user has been created in the admin tenant. Interactive and non-interactive creations are
     * both included.
     */
    ProductEvent["DeveloperCreated"] = "developer created";
    /** A user has been deleted in the admin tenant. */
    ProductEvent["DeveloperDeleted"] = "developer deleted";
    ProductEvent["AppCreated"] = "app created";
    ProductEvent["AppDeleted"] = "app deleted";
    ProductEvent["RoleCreated"] = "role created";
    ProductEvent["RoleDeleted"] = "role deleted";
    ProductEvent["ApiResourceCreated"] = "api resource created";
    ProductEvent["ApiResourceDeleted"] = "api resource deleted";
    ProductEvent["OrganizationCreated"] = "organization created";
    ProductEvent["OrganizationDeleted"] = "organization deleted";
    ProductEvent["OrganizationRoleCreated"] = "organization role created";
    ProductEvent["OrganizationRoleDeleted"] = "organization role deleted";
    ProductEvent["SsoConnectorCreated"] = "sso connector created";
    ProductEvent["SsoConnectorDeleted"] = "sso connector deleted";
    ProductEvent["PasswordlessConnectorUpdated"] = "passwordless connector updated";
    ProductEvent["SocialConnectorCreated"] = "connector created";
    ProductEvent["SocialConnectorDeleted"] = "connector deleted";
    ProductEvent["WebhookCreated"] = "webhook created";
    ProductEvent["WebhookDeleted"] = "webhook deleted";
    ProductEvent["CustomJwtDeployed"] = "custom jwt deployed";
    ProductEvent["MfaEnabled"] = "mfa enabled";
    ProductEvent["MfaDisabled"] = "mfa disabled";
    ProductEvent["CustomDomainCreated"] = "custom domain created";
    ProductEvent["CustomDomainDeleted"] = "custom domain deleted";
})(ProductEvent || (ProductEvent = {}));
/** The PostHog groups for product events. */
export var EventGroup;
(function (EventGroup) {
    EventGroup["Tenant"] = "tenant";
})(EventGroup || (EventGroup = {}));
/**
 * The static distinct ID for tenant-level events. This is used when the event is not
 * associated with a specific user.
 *
 * @see {@link https://posthog.com/docs/product-analytics/group-analytics#advanced-server-side-only-capturing-group-events-without-a-user}
 */
export const tenantEventDistinctId = 'TENANT_EVENT';
/**
 * The header that carries the cloud user ID in a request from Riven Auth Cloud. This is useful for
 * identifying the user who initiated the Management API request proxied by the cloud service.
 */
export const cloudUserIdHeader = 'riven-cloud-user-id';
